import React, { useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Card,
    CardActionArea,
    Chip,
    Grid,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EngineeringIcon from '@mui/icons-material/Engineering';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WalletIcon from '@mui/icons-material/Wallet';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import PolicyIcon from '@mui/icons-material/Policy';
import MessageIcon from '@mui/icons-material/Message';
import CameraEnhanceIcon from '@mui/icons-material/CameraEnhance';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { CreditCardOff, Campaign as CampaignIcon } from '@mui/icons-material';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';

// App hooks
import UserRole from '../../../shared/AuthRoles';
import {
    useGetTeamListQuery,
    useGetUserListQuery,
} from '../../../app/Slices/SevenDayApiSlice';
import { Notifications } from '@mui/icons-material';
import Person from '@mui/icons-material/Person';

type Item = {
    id: string; // unique key in UI
    name: string;
    description: string;
    url: string;
    icon: React.ReactNode;
    roles: string[];
    chip?: 'New' | 'Beta';
};

type Group = {
    group: string;
    items: Item[];
};

// ----- component -----
export default function AdminCentreList() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const navigate = useNavigate();
    const loggedInUser = UserRole();

    // Which users endpoint to hit (your original behavior preserved)
    const [userUrl, setUserUrl] = useState<string>('');
    useEffect(() => {
        if (
            ['guest_admin', 'guest_company', 'guest_department_admin'].includes(
                loggedInUser?.role,
            )
        ) {
            setUserUrl(`/guests/${loggedInUser?.company}/users`);
        } else {
            // Fallback for owners/admins if your slice supports it; otherwise leave empty.
            setUserUrl(`/users/${loggedInUser?.company}`);
        }
    }, [loggedInUser]);

    const { data: getUsers } = useGetUserListQuery(userUrl, { skip: !userUrl });
    const { data: getTeams } = useGetTeamListQuery(loggedInUser?.company, {
        skip: !loggedInUser?.company,
    });

    // Search & pinned state
    const [q, setQ] = useState('');

    // Navigate + GA
    const go = (url: string, title: string) => {
        navigate(url);
    };

    // ----- DATA -----
    const allSettings: Group[] = useMemo(
        () => [
            {
                group: 'Profile',
                items: [
                    {
                        id: 'host-profile',
                        name: 'Host Profile',
                        description:
                            "Manage your organisation's public profile",
                        url: `/hosts/${loggedInUser?.company}`,
                        icon: <Person />,
                        roles: ['owner_admin'],
                    },
                ],
            },
            {
                group: 'User Management',
                items: [
                    {
                        id: 'users',
                        name: 'Users',
                        description:
                            'Add, edit and manage users from your organisation',
                        url: '/users',
                        icon: <PeopleAltIcon />,
                        roles: ['owner_admin', 'admin', 'guest_admin'],
                    },
                    {
                        id: 'contractors',
                        name: 'Contractors',
                        description:
                            'Add, edit and manage connections to external contractors',
                        url: '/contractors',
                        icon: <EngineeringIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'teams',
                        name: 'Teams',
                        description:
                            'Add, edit and manage teams within your organisation',
                        url: '/teams',
                        icon: <AccountTreeIcon />,
                        roles: ['guest_admin'],
                    },
                    {
                        id: 'hosts',
                        name: 'Hosts',
                        description:
                            'Manage guest invoices and host bill settings',
                        url: '/clients',
                        icon: <ReceiptLongIcon />,
                        roles: ['admin'],
                    },
                    {
                        id: 'guests',
                        name: 'Guests',
                        description:
                            'Manage guest invoices and host bill settings',
                        url: '/guests',
                        icon: <ReceiptLongIcon />,
                        roles: ['admin'],
                    },
                    {
                        id: 'access',
                        name: 'User Access Control',
                        description:
                            'Control who can see listing prices based on roles, mark specific experiences as VIP-only, and limit the number of reservations users can make.',
                        url: '/accessControl',
                        icon: <PersonOffIcon />,
                        roles: ['guest_admin'],
                    },
                ],
            },
            {
                group: 'Vehicle Management',
                items: [
                    {
                        id: 'vehicles',
                        name: 'GPS and Vehicles',
                        description:
                            'Configure and manage GPS devices and vehicles',
                        url: '/vehicles',
                        icon: <GpsFixedIcon />,
                        roles: ['owner_admin'],
                    },
                ],
            },
            {
                group: 'Catering and Alcohol Settings',
                items: [
                    {
                        id: 'setMenus',
                        name: 'Catering and Alcohol Set Menus',
                        description:
                            'Configure and manage Catering and Alcohol Set Menus',
                        url: '/set-menus',
                        icon: <RestaurantMenuIcon />,
                        roles: ['owner_admin'],
                    },
                ],
            },
            {
                group: 'Finance and billing',
                items: [
                    {
                        id: 'billing',
                        name: 'Billing',
                        description: 'Manage billing account and view invoices',
                        url: '/billing',
                        icon: <CreditCardIcon />,
                        roles: ['guest_admin'],
                    },
                    {
                        id: 'bill-config',
                        name: 'Bills and Invoices Configurations',
                        description:
                            'Manage guest invoices and host bill settings',
                        url: '/bills-invoices',
                        icon: <ReceiptLongIcon />,
                        roles: ['admin'],
                    },
                    {
                        id: 'app-fees',
                        name: '7DAY App Fees',
                        description: 'Manage 7DAY App fees to Hosts and Guests',
                        url: '/7dayfees',
                        icon: <AttachMoneyIcon />,
                        roles: ['admin'],
                    },
                    {
                        id: 'credit-holds',
                        name: 'Credit Hold Manager',
                        description:
                            'Manage client account holds for overdue or unpaid balances. Admins can place clients on credit hold, edit or remove existing holds, and view the full list of accounts currently restricted. While on hold, clients cannot make new bookings until payment is received.',
                        url: '/credit-holds',
                        icon: <CreditCardOff />,
                        roles: ['admin'],
                    },
                    {
                        id: 'expense-claims',
                        name: 'Expense Claims',
                        description:
                            'Attach expense claims to bookings for reimbursement',
                        url: '/expenseClaims',
                        icon: <ReceiptIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'billing-exclusions',
                        name: 'Billing Exclusions',
                        description:
                            'Easily exclude selected guests from billing for specific experiences or all bookings.',
                        url: '/billingExclusions',
                        icon: <PersonOffIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'payout-details',
                        name: 'Payout Details',
                        description:
                            'Securely store your bank details to get paid for hosting and expenses.',
                        url: '/payoutDetails',
                        icon: <WalletIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'earnings',
                        name: 'Reservation Earnings',
                        description:
                            'Overview of your earnings from listed experiences',
                        url: '/earnings',
                        icon: <TrendingUpIcon />,
                        roles: ['owner_admin'],
                    },
                ],
            },
            {
                group: 'Release Notes',
                items: [
                    {
                        id: 'release-notes',
                        name: 'Release Notes Prompt',
                        description:
                            'Configure the in-app announcement shown to users after a new release',
                        url: '/release-notes',
                        icon: <CampaignIcon />,
                        roles: ['admin'],
                    },
                ],
            },
            {
                group: 'AI',
                items: [
                    {
                        id: 'ai-history',
                        name: 'AI History',
                        description:
                            'View the conversation history between users and the AI reservation assistant',
                        url: '/ai-history',
                        icon: <ChatBubbleOutlineIcon />,
                        roles: ['admin'],
                        chip: 'Beta',
                    },
                ],
            },
            {
                group: 'Platform Settings',
                items: [
                    {
                        id: 'caps',
                        name: 'Booking Caps',
                        description: 'Set limits on how often a user can book',
                        url: '/bookingCaps',
                        icon: <CalendarMonthIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'fee-types',
                        name: 'Fee Types',
                        description: 'Create and apply custom fee types',
                        url: '/fees',
                        icon: <PriceChangeIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'policies',
                        name: 'Your Policies',
                        description:
                            'Create custom policies for your experiences',
                        url: '/policies',
                        icon: <PolicyIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'messaging',
                        name: 'In-app Messaging',
                        description:
                            'Enable seamless communication through the platform',
                        url: '/messaging',
                        icon: <MessageIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'notifications',
                        name: 'Notifications',
                        description:
                            'Customise your company notifications and reminders',
                        url: '/adminCentre/notifications',
                        icon: <Notifications />,
                        roles: ['owner_admin'],
                    },
                ],
            },
            {
                group: 'Connected Apps',
                items: [
                    {
                        id: 'nirovision',
                        name: 'Nirovision Face Rec Access',
                        description: 'Enable secure facial recognition access',
                        url: '/nirovision',
                        icon: <CameraEnhanceIcon />,
                        roles: ['owner_admin'],
                    },
                    {
                        id: 'xero-network-key',
                        name: 'Xero to Xero',
                        description:
                            'Link your Xero account to receive draft invoices directly in Xero. No manual entry—keep everything synced.',
                        url: '/xeroNetworkKey',
                        icon: <VpnKeyIcon />,
                        roles: ['owner_admin'],
                    },
                ],
            },
        ],
        [],
    );

    // Filter by role then by search
    const groups = useMemo(() => {
        const role = loggedInUser?.role || 'default';
        const s = q.trim().toLowerCase();

        const byRole = allSettings
            .map((g) => ({
                ...g,
                items: g.items.filter((it) => it.roles.includes(role)),
            }))
            .filter((g) => g.items.length > 0);

        if (!s) return byRole;

        return byRole
            .map((g) => ({
                ...g,
                items: g.items.filter(
                    (it) =>
                        it.name.toLowerCase().includes(s) ||
                        it.description.toLowerCase().includes(s) ||
                        g.group.toLowerCase().includes(s),
                ),
            }))
            .filter((g) => g.items.length > 0);
    }, [allSettings, loggedInUser?.role, q]);

    // “Needs setup” conditions (preserving your logic)
    const needsSetup = (itemId: string) => {
        if (itemId === 'users') return (getUsers?.length ?? 0) === 1;
        if (itemId === 'teams') return (getTeams?.length ?? 0) === 0;
        return false;
    };

    // Layout helpers
    const cols = isMobile ? 1 : isIpad ? 2 : 3;

    return (
        <Box sx={{ width: '100%', mt: 3 }}>
            {/* Search */}
            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder='Search settings…'
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon sx={{ color: 'text.primary' }} />
                            </InputAdornment>
                        ),
                    }}
                    size='small'
                />
            </Box>

            {groups.map((group) => (
                <Box key={group.group} sx={{ mb: 4 }}>
                    <Typography
                        component={'h2'}
                        sx={{
                            color: 'text.primary',
                            fontWeight: 'bold',
                        }}
                    >
                        {group.group}
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {group.items.map((it) => (
                            <Grid item key={it.id} xs={12}>
                                <SettingCard
                                    item={it}
                                    onClick={() => go(it.url, it.name)}
                                    showBadge={needsSetup(it.id)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            ))}
        </Box>
    );
}

// ----- Setting Card -----
function SettingCard({
    item,

    onClick,
    showBadge,
}: {
    item: Item;

    onClick: () => void;
    showBadge: boolean;
}) {
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                transition: '0.2s ease',
                '&:hover': {
                    boxShadow: 3,
                    borderColor: 'primary.light',
                },
            }}
        >
            <CardActionArea
                onClick={onClick}
                sx={{
                    p: 2,
                    alignItems: 'stretch',
                }}
            >
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: 'grid',
                            placeItems: 'center',
                            bgcolor: 'lightGrey.main',
                            color: 'lightGrey.contrastText',
                        }}
                    >
                        {showBadge ? (
                            <Badge
                                color='error'
                                overlap='circular'
                                badgeContent='!'
                            >
                                <Box
                                    sx={{
                                        display: 'grid',
                                        placeItems: 'center',
                                    }}
                                >
                                    {item.icon}
                                </Box>
                            </Badge>
                        ) : (
                            item.icon
                        )}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <Typography
                                component={'h3'}
                                variant='subtitle1'
                                noWrap
                            >
                                {item.name}
                            </Typography>
                            {item.chip && (
                                <Chip
                                    label={item.chip}
                                    size='small'
                                    color={
                                        item.chip === 'New'
                                            ? 'success'
                                            : 'warning'
                                    }
                                    variant='outlined'
                                />
                            )}
                        </Stack>
                        <Typography
                            variant='body3'
                            color='text.primary'
                            sx={{ mt: 0.25 }}
                            noWrap
                        >
                            {item.description}
                        </Typography>
                    </Box>

                    <Stack alignItems='flex-end' spacing={1}>
                        <ArrowForwardIosRoundedIcon
                            sx={{
                                color: 'text.disabled',
                                fontSize: 16,
                                mt: 'auto',
                            }}
                        />
                    </Stack>
                </Stack>
            </CardActionArea>
        </Card>
    );
}
